import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type TrackingSettingsResult } from '@whinerp/client';
import { Button, Panel, SectionLabel, Skeleton, ErrorState } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * How locations are resolved, and how long addresses are kept (F-152).
 *
 * The provider's mode is stated rather than inferred from empty chips. "A
 * provider resolved this", "this is a sandbox estimate" and "nothing is
 * configured" are three different things for an administrator to do something
 * about, and a blank panel is all three.
 */
export function TrackingPanel(): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();

  const settings = useQuery<TrackingSettingsResult, ApiError>({
    queryKey: ['tracking'],
    queryFn: () => api.trackingSettings({}),
  });

  const refresh = (): Promise<unknown> => queryClient.invalidateQueries({ queryKey: ['tracking'] });

  const retention = useMutation<unknown, ApiError, number>({
    mutationFn: (days) => api.setIpRetention({ body: { days } }),
    onSuccess: () => refresh(),
  });

  const drop = useMutation<unknown, ApiError>({
    mutationFn: () => api.dropExpiredAddresses({}),
    onSuccess: () => refresh(),
  });

  if (settings.isPending) return <Skeleton className="h-block-md w-full" />;
  /*
   * A failure is not a loading state.
   *
   * These returned a Skeleton, so a panel whose request failed showed a
   * shimmer for ever - a screen that never loads rather than one that says
   * what went wrong, and a `settled()` that never settles (D-365).
   */
  if (settings.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={settings.error.message}
        onRetry={() => void settings.refetch()}
      />
    );
  }

  const { provider, ipRetentionDays } = settings.data;
  const failure = retention.error ?? drop.error;

  return (
    <Panel data-region="tracking" id="tracking">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('geo.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('geo.body')}</p>
          <p data-tracking-provider={provider.mode} className="text-dense text-ink">
            {provider.mode === 'live'
              ? t('geo.provider.live', { values: { source: provider.label } })
              : provider.mode === 'sandbox'
                ? t('geo.provider.sandbox')
                : t('geo.provider.none')}
          </p>
          {/* What to do about it, when there is something to do. */}
          {provider.nextStep ? (
            <p data-tracking-next-step className="text-meta text-ink-meta">
              {provider.nextStep}
            </p>
          ) : null}
        </div>

        {failure ? (
          <p
            data-tracking-refusal
            className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text"
          >
            {failure.message}
          </p>
        ) : null}

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex items-center gap-2 text-dense text-ink-600">
            {t('geo.retention')}
            <input
              data-tracking-retention
              inputMode="numeric"
              className="w-col-count rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              defaultValue={String(ipRetentionDays)}
              onBlur={(event) => {
                const days = Number(event.target.value);
                if (days === ipRetentionDays) return;
                retention.mutate(days);
              }}
            />
            {t('geo.days')}
          </label>
          <Button intent="ghost" size="small" data-tracking-drop onClick={() => drop.mutate()}>
            {t('geo.dropNow')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
